import { useEffect, useState } from "react";
import { AnimatedCounter } from "../animate-counter";
import boltGradientSvg from "~/assets/bolt-gradient.svg";
import chartBarGradientSvg from "~/assets/chart-bar-gradient.svg";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@mindstonehq/ui";
import { graphql } from "~/graphql/codegen";
import { useSubscription } from "@apollo/client/react";
import type {
  PointsEarnedSubscription,
  PointsEarnedSubscriptionVariables,
} from "~/graphql/codegen/graphql";

const POINTS_EARNED = graphql(`
  subscription PointsEarned {
    pointsEarned {
      updatedEvidencePoints
    }
  }
`);
import { useCurrentSpace, useProfile, useUser } from "~/hooks/context-hook";
import { useIdleRevalidate } from "~/hooks/use-idle-revalidate";
import { isMobile } from "~/utils/device-check";
import metrics from "~/utils/metrics";
import { useHoverPopover } from "~/hooks/useHoverPopover";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

type PointsAndLevelDisplayProps = {
  variant: "mobile" | "desktop";
};

export function PointsAndLevelDisplay({ variant }: PointsAndLevelDisplayProps) {
  const { t } = useLingui();
  const isMobileDevice = isMobile();

  const user = useProfile();
  const currentSpace = useCurrentSpace();
  // Idle-gated revalidate — see use-idle-revalidate.ts (incident 2026-07-06).
  // The points-earned WS onData and the levelChange listener below both call
  // this; gating on an idle router stops them re-requesting /sandbox.data while
  // a loader auth-redirect is pending (the runaway-loop vector).
  const revalidate = useIdleRevalidate();

  const [prevLevel, setPrevLevel] = useState(user?.level?.[0]?.level || 0);
  const [prevPoints, setPrevPoints] = useState(user?.evidencing?.points || 0);

  const {
    isShown: showEPTooltip,
    handleOpen: handleEPTooltipOpen,
    handleClose: handleEPTooltipClose,
    handleOpenChange: handleEPTooltipOpenChange,
  } = useHoverPopover(300);

  const {
    isShown: showLevelTooltip,
    handleOpen: handleLevelTooltipOpen,
    handleClose: handleLevelTooltipClose,
    handleOpenChange: handleLevelTooltipOpenChange,
  } = useHoverPopover(300);

  // const { data: breakdownData, loading: breakdownLoading } =
  //   usePointsBreakdownQuery({
  //     variables: {
  //       identifier: currentSpace.uniqueName,
  //     },
  //     skip: !showLevelTooltip,
  //   });

  const [currentPoints, setCurrentPoints] = useState(
    user?.evidencing?.points || 0,
  );

  // event listener so we can refetch when level changes from the sandbox
  useEffect(() => {
    const handleLevelChange = () => {
      revalidate();
    };
    window.addEventListener("levelChange", handleLevelChange);
    return () => window.removeEventListener("levelChange", handleLevelChange);
  }, []);

  // A level that couldn't be read (null, not an empty list) is never shown as
  // 0: the counter and its hint are left out. An empty list is "no level yet".
  const levelRead = user?.level !== null;
  const promptLevel = user?.level?.[0]?.level ?? 0;
  const evidencePoints = user?.evidencing?.points || 0;

  useEffect(() => {
    if (showEPTooltip) {
      metrics.track("ep_tooltip_shown");
    }
    if (showLevelTooltip) {
      metrics.track("level_tooltip_shown");
    }
  }, [showEPTooltip, showLevelTooltip]);

  // Subscribe to points earned in cohort. A points_earned event only signals
  // that the server-side EP total changed; we deliberately do NOT add
  // updatedEvidencePoints to the displayed value. The authoritative total
  // (evidencing.points -> getPoints) is the sum of per-objective achievement
  // progress, so a single credit's delta does not equal the total increment and
  // adding it here double-counts (see docs/plans/260619_sandbox-ep-double-count.md).
  // Instead revalidate so evidencePoints refreshes and the counter animates once
  // to the true total.
  useSubscription<PointsEarnedSubscription, PointsEarnedSubscriptionVariables>(
    POINTS_EARNED,
    {
      onData: () => {
        revalidate();
      },
    },
  );

  useEffect(() => {
    if (promptLevel !== prevLevel) {
      setPrevLevel(promptLevel);
    }
    if (evidencePoints !== prevPoints) {
      setPrevPoints(evidencePoints);
      setCurrentPoints(evidencePoints);
    }
  }, [promptLevel, evidencePoints]);

  return (
    <div className="flex items-center space-x-4">
      <Popover open={showEPTooltip} onOpenChange={handleEPTooltipOpenChange}>
        <PopoverTrigger asChild>
          <div
            className="flex items-center space-x-1 cursor-pointer"
            onMouseEnter={handleEPTooltipOpen}
            onMouseLeave={handleEPTooltipClose}
          >
            <img
              src={boltGradientSvg}
              className="h-5 w-5"
              alt={t(msg`Evidence Points`)}
            />
            <span className="text-sm font-medium">
              <AnimatedCounter
                from={prevPoints}
                to={currentPoints}
                duration={1}
              />
            </span>
          </div>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          className="bg-gradient-to-br from-blue-300 via-purple-300 to-purple-400 p-[2px]"
          onMouseEnter={handleEPTooltipOpen}
          onMouseLeave={handleEPTooltipClose}
        >
          <div className="flex items-start gap-3 p-4 max-w-[300px] bg-white dark:bg-card rounded-md">
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <p className="font-bold text-foreground">
                  <Trans>Your EP (Evidence Points)</Trans>
                </p>
              </div>
              <p className="text-xs font-medium text-muted-foreground">
                <Trans>Earn EP by completing trivia and practicing in the sandbox. Higher quality answers like detailed trivia or well-thought-out prompts earn more points, rewarding effort and accuracy.</Trans>
              </p>
              <p className="text-xs font-medium text-muted-foreground">
                <Trans>
                  Learn more about how EP works{" "}
                  <a
                    href="https://help.mindstone.com/what-are-evidence-points"
                    className="underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                </Trans>
              </p>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {levelRead && (
        <Popover
          open={showLevelTooltip}
          onOpenChange={handleLevelTooltipOpenChange}
        >
          <PopoverTrigger asChild>
            <div
              className="flex items-center space-x-1 cursor-pointer"
              onMouseEnter={handleLevelTooltipOpen}
              onMouseLeave={handleLevelTooltipClose}
            >
              <img
                src={chartBarGradientSvg}
                className="h-5 w-5"
                alt={t(msg`Level`)}
              />
              <span className="text-sm font-medium">
                <AnimatedCounter from={prevLevel} to={promptLevel} duration={1} />
              </span>
            </div>
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="end"
            className="bg-gradient-to-br from-blue-300 via-purple-300 to-purple-400 p-[2px] w-[400px]"
            onMouseEnter={handleLevelTooltipOpen}
            onMouseLeave={handleLevelTooltipClose}
          >
            <div className="flex flex-col items-start gap-3 p-4 bg-white dark:bg-card rounded-md">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <p className="font-bold text-foreground"><Trans>Your Prompt Level</Trans></p>
                </div>
                <p className="text-xs font-medium text-muted-foreground">
                  {promptLevel === 0 && (
                    <>
                      <Trans>After you've done your first 10 prompts your prompt level will appear.</Trans>
                      <br />
                    </>
                  )}
                  <Trans>Your prompt level is determined by the overall average score of all your prompts in the Sandbox. As you continue to practice, your level can increase or decrease based on the quality of your prompts.</Trans>
                </p>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
